import { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { ApiError, createSession, getSession, listSessions, postChat } from '../api.ts';
import type { ChatMessage, SessionDetail, SessionSummary } from '../types.ts';
import { navigate } from '../lib/router.ts';
import { StatePanel } from '../components/StatePanel.tsx';
import { PhaseBanner } from '../components/PhaseBanner.tsx';
import { ArtifactBanner } from '../components/ArtifactBanner.tsx';
import { ToolChip, type ToolActivity } from '../components/ToolChip.tsx';

/**
 * Whether the viewer may read this session and not chat in it (docs/specs/accounts.md, section 3).
 * That is an admin in another person's session, which is where the admin runs page leads. The
 * server says so on the session (`access`). `refused` is the fallback: a chat turn that came back
 * 403 proves the same thing, whatever the session said.
 */
export function chatIsReadOnly(session: Pick<SessionDetail, 'access'> | null, refused: boolean): boolean {
  return refused || session?.access === 'read_only';
}

/**
 * What stands where the composer would be for a viewer who may only read. No input is offered: a
 * field that can only answer "you may not" is not a field.
 */
export function ReadOnlyComposer(): JSX.Element {
  return (
    <div className="composer composer-readonly" role="note">
      <span className="key k-unknown">read only</span>
      <p className="muted">This session belongs to someone else. An admin can read it, and cannot chat in it.</p>
    </div>
  );
}

/**
 * Chat view (docs/specs/app.md, apps/web §1): session sidebar, message thread with tool
 * activity chips, right-hand state panel, phase banner when a run is active.
 */
export function ChatView({ sessionId }: { sessionId: string | null }): JSX.Element {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [liveActivity, setLiveActivity] = useState<ToolActivity[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** The server answered a chat turn with 403: this session is somebody else's. */
  const [refused, setRefused] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);

  const refreshSessions = useCallback(() => {
    listSessions().then(setSessions).catch(() => undefined);
  }, []);

  const refreshSession = useCallback(async () => {
    if (!sessionId) return;
    const s = await getSession(sessionId);
    setSession(s);
  }, [sessionId]);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  useEffect(() => {
    setSession(null);
    setRefused(false);
    setLiveText('');
    setLiveActivity([]);
    if (sessionId) refreshSession().catch((e) => setError(String(e)));
  }, [sessionId, refreshSession]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: 'end' });
  }, [session?.messages.length, liveText, liveActivity.length]);

  async function handleNewSession(): Promise<void> {
    const { session_id } = await createSession();
    refreshSessions();
    navigate({ view: 'session', sessionId: session_id });
  }

  async function handleSend(): Promise<void> {
    const message = input.trim();
    if (!message || !sessionId || sending) return;
    setInput('');
    setSending(true);
    setLiveText('');
    setLiveActivity([]);
    setError(null);
    try {
      await postChat(sessionId, message, (evt) => {
        switch (evt.type) {
          case 'assistant_text':
            setLiveText((t) => t + evt.text);
            break;
          case 'tool_call':
            setLiveActivity((a) => [...a, { kind: 'call', name: evt.name, text: evt.args_summary }]);
            break;
          case 'tool_result':
            setLiveActivity((a) => [...a, { kind: 'result', name: evt.name, text: evt.summary }]);
            break;
          case 'state_changed':
            refreshSession().catch(() => undefined);
            break;
          case 'done':
            break;
        }
      });
    } catch (e) {
      // 403: the session belongs to someone else. The composer gives way to the read-only note,
      // which says so in the app's own words. The raw response body is not shown as an error.
      if (e instanceof ApiError && e.status === 403) setRefused(true);
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
      setLiveText('');
      setLiveActivity([]);
      await refreshSession().catch(() => undefined);
      refreshSessions();
    }
  }

  return (
    <div className="chat-layout">
      <aside className="chat-sessions">
        <div className="chat-sessions-new">
          <button className="btn primary sm" onClick={() => void handleNewSession()}>
            + New session
          </button>
        </div>
        <div className="chat-sessions-head">
          <span className="label">Sessions</span>
        </div>
        <ul className="session-list">
          {sessions.map((s) => (
            <li key={s.id} className={`session-item${s.id === sessionId ? ' active' : ''}`}>
              <a className="session-link" href={`#/sessions/${s.id}`} title={s.title}>
                <span className="session-title">{s.title || '(untitled)'}</span>
                <span className="session-phase">{s.phase}</span>
              </a>
              {/* Reachability addendum (v1.1): the mark deep-links straight to the artifact —
                  phase 'analysis' implies it exists. Falls back to the session view for stale
                  summaries missing run_id (older server payloads). */}
              {s.phase === 'analysis' && (
                <a
                  className="session-artifact-mark"
                  href={s.run_id ? `#/sessions/${s.id}/runs/${s.run_id}/artifact` : `#/sessions/${s.id}`}
                  title="Artifact ready"
                >
                  artifact
                </a>
              )}
            </li>
          ))}
        </ul>
      </aside>

      <main className="chat-thread-col grat">
        {!sessionId && <p className="muted chat-blank">Select a session, or start a new one.</p>}

        {sessionId && !session && !error && <p className="muted chat-blank">Loading…</p>}

        {error && <p className="error chat-blank">{error}</p>}

        {session && (
          <>
            {session.phase === 'running' && session.run_id && (
              <div className="chat-banners">
                <PhaseBanner sessionId={session.id} runId={session.run_id} />
              </div>
            )}
            {session.phase === 'analysis' && session.run_id && (
              <div className="chat-banners">
                <ArtifactBanner sessionId={session.id} runId={session.run_id} />
              </div>
            )}

            <div className="thread">
              {session.messages.map((m, i) => (
                <MessageBubble key={i} message={m} />
              ))}

              {sending && (
                <div className="turn">
                  {liveActivity.length > 0 && (
                    <div className="tool-row">
                      {liveActivity.map((a, i) => (
                        <ToolChip key={i} activity={a} />
                      ))}
                    </div>
                  )}
                  {liveText && (
                    <div className="msg msg-assistant panel msg-streaming">
                      <div className="msg-md">
                        <Markdown>{liveText}</Markdown>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div ref={threadEndRef} />
            </div>

            {chatIsReadOnly(session, refused) ? (
              <ReadOnlyComposer />
            ) : (
              <form
                className="composer"
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleSend();
                }}
              >
                <div className="composer-row">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={session.phase === 'analysis' ? 'Ask about the artifact…' : 'Tell me about your product…'}
                    rows={2}
                    disabled={sending}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void handleSend();
                      }
                    }}
                  />
                  <button className="btn primary" type="submit" disabled={sending || !input.trim()}>
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
                <span className="composer-hint">Enter to send · Shift+Enter for a new line</span>
              </form>
            )}
          </>
        )}
      </main>

      {session && (
        <StatePanel
          product={session.product}
          competitors={session.competitors}
          phase={session.phase}
          sessionId={session.id}
          runId={session.run_id}
        />
      )}
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }): JSX.Element {
  return (
    <div className={`msg-row ${message.role}`}>
      <div className={message.role === 'user' ? 'msg msg-user' : 'msg msg-assistant panel'}>
        {message.role === 'user' ? (
          <p>{message.content}</p>
        ) : (
          <div className="msg-md">
            <Markdown>{message.content}</Markdown>
          </div>
        )}
      </div>
    </div>
  );
}
